import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";

export const CartDrawer = () => {
  const {
    items,
    isOpen,
    closeCart,
    updateItem,
    removeItem,
    subtotal,
    count,
  } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    if (items.length === 0) return;
    closeCart();
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={(v) => !v && closeCart()}>
      <SheetContent
        side="right"
        className="bg-[#0A0A0A] border-l border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-8 pb-4 border-b border-white/10">
          <SheetTitle className="font-display text-2xl tracking-wider uppercase text-white">
            Your Arsenal
            <span className="ml-2 text-white/40 text-base">({count})</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <div className="font-display text-3xl tracking-wider uppercase mb-3">
                Empty
              </div>
              <p className="text-white/50 text-sm">
                Add a product to begin the protocol.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((i) => (
                <li
                  key={i.product_id}
                  className="py-5 flex gap-4"
                  data-testid={`cart-item-${i.product_id}`}
                >
                  <div className="w-20 h-20 bg-[#111] border border-white/10 shrink-0 overflow-hidden">
                    <img
                      src={i.image}
                      alt={i.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display text-lg tracking-wider uppercase leading-none">
                          {i.name}
                        </div>
                        <div className="text-white/50 text-xs mt-1">
                          ${i.price.toFixed(2)} each
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(i.product_id)}
                        data-testid={`remove-${i.product_id}-button`}
                        className="text-white/40 hover:text-[#D10000]"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center border border-white/10">
                        <button
                          onClick={() =>
                            updateItem(i.product_id, i.quantity - 1)
                          }
                          data-testid={`decrease-${i.product_id}-button`}
                          className="w-8 h-8 grid place-items-center hover:bg-white/5"
                          aria-label="Decrease"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center font-display tracking-wider">
                          {i.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateItem(i.product_id, i.quantity + 1)
                          }
                          data-testid={`increase-${i.product_id}-button`}
                          className="w-8 h-8 grid place-items-center hover:bg-white/5"
                          aria-label="Increase"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="font-display text-lg tracking-wider">
                        ${(i.price * i.quantity).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-white/10 px-6 py-6 space-y-4">
          <div className="flex items-center justify-between text-sm text-white/60">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal" className="text-white font-display text-xl tracking-wider">
              ${subtotal.toFixed(2)}
            </span>
          </div>
          <p className="text-xs text-white/40">
            Shipping & taxes calculated at secure checkout.
          </p>
          <button
            onClick={goToCheckout}
            disabled={items.length === 0}
            data-testid="checkout-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D10000] hover:bg-[#A30000] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display text-xl tracking-widest uppercase py-4 transition-colors"
          >
            Continue to Checkout
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
